import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import AlphaCoreLogo from "@/components/AlphaCoreLogo";
import InstallAlphaCorePrompt from "@/components/InstallAlphaCorePrompt";
import { mergeModules, timeAgo } from "@/lib/modules";
import {
  LayoutDashboard, FolderKanban, HardDrive, CreditCard, Settings, LogOut, Coins,
  ChevronRight, MessageCircle, Shield, Menu, Home as HomeIcon, Plus, Search as SearchIcon,
  Bell, Crown, HelpCircle, User as UserIcon, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const MANAGE = [
  { to: "/projects", icon: FolderKanban, label: "Projects" },
  { to: "/files", icon: HardDrive, label: "Files" },
  { to: "/billing", icon: CreditCard, label: "Credits" },
];
const FOOTER_NAV = [
  { to: "/settings", icon: Settings, label: "Settings" },
  { to: "/support", icon: HelpCircle, label: "Help Center" },
];

const STATUS_BADGE = { beta: "BETA", maintenance: "MAINTENANCE", coming_soon: "SOON" };

function ModuleNavItem({ m, onNavigate }) {
  const badge = STATUS_BADGE[m.status];
  const disabled = m.status === "maintenance" || m.status === "coming_soon";
  const Icon = m.icon;
  const body = ({ isActive }) => (
    <>
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.7} style={{ color: isActive || !disabled ? `rgb(${m.rgb})` : undefined }} />
      <span className={`truncate ${isActive ? "text-white" : ""}`}>{m.name}</span>
      {badge && <span className="ml-auto text-[9px] font-mono tracking-wider px-1.5 py-0.5 rounded-sm border border-white/15 text-white/50">{badge}</span>}
      {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-md" style={{ background: `rgb(${m.rgb})` }} />}
    </>
  );
  if (disabled) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/35 cursor-not-allowed min-h-[44px] relative" data-testid={`nav-module-${m.key}`}>
        {body({ isActive: false })}
      </div>
    );
  }
  return (
    <NavLink
      to={m.route}
      onClick={onNavigate}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors relative min-h-[44px] ${
          isActive ? "bg-white/[0.05] text-white" : "text-white/60 hover:text-white hover:bg-white/[0.03]"
        }`
      }
      data-testid={`nav-module-${m.key}`}
    >
      {body}
    </NavLink>
  );
}

function PlainNavItem({ item, onNavigate }) {
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors relative min-h-[44px] ${
          isActive ? "bg-white/[0.05] text-white" : "text-white/60 hover:text-white hover:bg-white/[0.03]"
        }`
      }
      data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "")}`}
    >
      <item.icon className="h-4 w-4" strokeWidth={1.7} />
      <span>{item.label}</span>
    </NavLink>
  );
}

function NavList({ modules, onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto py-4 px-3">
      <PlainNavItem item={{ to: "/dashboard", icon: HomeIcon, label: "Dashboard" }} onNavigate={onNavigate} />
      <div className="space-y-0.5 mt-1">
        {modules.map((m) => <ModuleNavItem key={m.key} m={m} onNavigate={onNavigate} />)}
      </div>
      <div className="my-4 border-t border-white/[0.06]" />
      <div className="space-y-0.5">
        {MANAGE.map((i) => <PlainNavItem key={i.to} item={i} onNavigate={onNavigate} />)}
      </div>
      <div className="my-4 border-t border-white/[0.06]" />
      <div className="space-y-0.5">
        {FOOTER_NAV.map((i) => <PlainNavItem key={i.to} item={i} onNavigate={onNavigate} />)}
      </div>
    </nav>
  );
}

function CreditsCard({ onGo }) {
  const { user } = useAuth();
  return (
    <div className="kr-card p-3 relative overflow-hidden">
      <div className="absolute inset-x-0 -top-px h-[2px] ac-rainbow-bg opacity-70" />
      <div className="flex items-center justify-between">
        <span className="kr-overline">CREDITS</span>
        <Coins className="h-3.5 w-3.5" style={{ color: "rgb(var(--ac-yellow))" }} />
      </div>
      <div className="font-display text-2xl mt-1.5 ac-rainbow-text" data-testid="sidebar-credits">{user?.credits?.toLocaleString() ?? 0}</div>
      <Button onClick={onGo} variant="outline" className="w-full mt-3 h-9 border-white/10 text-xs hover:bg-white/5 hover:text-white" data-testid="sidebar-upgrade-btn">
        Top up <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

function GlobalSearch({ modules }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); inputRef.current?.focus(); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onFocus = () => {
    setOpen(true);
    if (projects === null) api.get("/projects").then((r) => setProjects(r.data.items || [])).catch(() => setProjects([]));
  };

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const needle = q.toLowerCase();
    const mods = modules.filter((m) => m.name.toLowerCase().includes(needle)).map((m) => ({ type: "Module", label: m.name, to: m.route, rgb: m.rgb }));
    const projs = (projects || []).filter((p) => (p.name || "").toLowerCase().includes(needle)).slice(0, 5)
      .map((p) => ({ type: "Project", label: p.name, to: `/projects/${p.project_id}` }));
    return [...mods, ...projs].slice(0, 8);
  }, [q, modules, projects]);

  const go = (r) => { setOpen(false); setQ(""); navigate(r.to); };

  return (
    <div className="relative w-full max-w-md">
      <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 h-9">
        <SearchIcon className="h-3.5 w-3.5 text-white/40" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={onFocus}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter" && results[0]) go(results[0]); }}
          placeholder="Search anything…"
          className="bg-transparent outline-none text-sm flex-1 placeholder:text-white/30"
          data-testid="global-search-input"
        />
        <kbd className="text-[10px] font-mono text-white/30 border border-white/10 rounded px-1.5 py-0.5">Ctrl K</kbd>
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-11 inset-x-0 kr-card bg-[#101219] border-white/10 py-1.5 z-50 shadow-2xl" data-testid="global-search-results">
          {results.map((r, i) => (
            <button key={i} onMouseDown={() => go(r)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-white/75 hover:bg-white/[0.05] hover:text-white text-left">
              <span className="text-[9px] font-mono tracking-wider text-white/35 w-14 shrink-0">{r.type.toUpperCase()}</span>
              <span className="truncate" style={r.rgb ? { color: `rgb(${r.rgb})` } : undefined}>{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationsBell() {
  const [items, setItems] = useState(null);
  const load = () => {
    if (items === null) api.get("/dashboard/smart").then((r) => setItems(r.data.recent_activity || [])).catch(() => setItems([]));
  };
  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button className="relative min-w-[40px] min-h-[40px] flex items-center justify-center rounded-full hover:bg-white/5 text-white/60 hover:text-white" aria-label="Notifications" data-testid="notifications-bell">
          <Bell className="h-4.5 w-4.5 h-[18px] w-[18px]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#101219] border-white/10 text-white w-80">
        <DropdownMenuLabel className="kr-overline">RECENT ACTIVITY</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/5" />
        {(items || []).slice(0, 6).map((a) => (
          <div key={a.event_id} className="px-2 py-2 text-xs text-white/70 flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full ac-rainbow-bg mt-1.5 shrink-0" />
            <div className="min-w-0">
              <div className="truncate">{(a.target_name || a.action || "").slice(0, 60)}</div>
              <div className="text-white/35 mt-0.5">{a.module} · {timeAgo(a.created_at)}</div>
            </div>
          </div>
        ))}
        {items !== null && items.length === 0 && <div className="px-3 py-6 text-center text-xs text-white/35">No activity yet.</div>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const CREATE_SHEET_KEYS = ["image-studio", "website-builder", "research", "swarm", "code"];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [modules, setModules] = useState(() => mergeModules(null));

  useEffect(() => {
    api.get("/modules").then((r) => setModules(mergeModules(r.data.items))).catch(() => {});
  }, []);

  const segs = location.pathname.split("/").filter(Boolean);
  const pageTitle = segs.length === 0 ? "Dashboard" : (segs[segs.length - 1] || "").replace(/-/g, " ");

  useEffect(() => { setDrawerOpen(false); setCreateOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (drawerOpen || createOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [drawerOpen, createOpen]);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const createModules = modules.filter((m) => CREATE_SHEET_KEYS.includes(m.key));

  return (
    <div className="app-shell bg-[#07080B] text-white flex overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 h-full min-h-0 border-r border-white/[0.06] bg-[#07080B] flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center px-4 border-b border-white/[0.06]">
          <Link to="/dashboard"><AlphaCoreLogo size={38} /></Link>
        </div>
        <NavList modules={modules} />
        <div className="border-t border-white/[0.06] p-3">
          <CreditsCard onGo={() => navigate("/billing")} />
        </div>
      </aside>

      {/* Mobile Nav Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[86vw] max-w-[340px] p-0 bg-[#07080B] border-r border-white/10 text-white flex flex-col" data-testid="mobile-drawer">
          <SheetTitle className="sr-only">AlphaCore navigation</SheetTitle>
          <SheetDescription className="sr-only">Primary application menu with links to every module.</SheetDescription>
          <div className="h-14 flex items-center px-4 border-b border-white/[0.06]">
            <AlphaCoreLogo size={32} />
          </div>
          <NavList modules={modules} onNavigate={() => setDrawerOpen(false)} />
          <div className="border-t border-white/[0.06] p-3 space-y-2">
            <InstallAlphaCorePrompt variant="drawer" />
            <CreditsCard onGo={() => { setDrawerOpen(false); navigate("/billing"); }} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Create sheet (mobile FAB) */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="bottom" className="bg-[#0B0D12] border-t border-white/10 text-white pb-[calc(env(safe-area-inset-bottom)+24px)] rounded-t-2xl" data-testid="create-sheet">
          <SheetTitle className="kr-overline mb-4 text-left ac-rainbow-text">CREATE</SheetTitle>
          <SheetDescription className="sr-only">Start creating in one of AlphaCore's modules.</SheetDescription>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {createModules.map((m) => (
              <Link
                key={m.key}
                to={m.route}
                onClick={() => setCreateOpen(false)}
                className="p-4 rounded-xl flex flex-col items-start gap-3 min-h-[92px] border transition-colors"
                style={{ borderColor: `rgba(${m.rgb.split(" ").join(",")},0.3)`, background: `linear-gradient(140deg, rgba(${m.rgb.split(" ").join(",")},0.09), rgba(255,255,255,0.01))` }}
                data-testid={`create-sheet-${m.key}`}
              >
                <m.icon className="h-5 w-5" style={{ color: `rgb(${m.rgb})` }} />
                <div className="text-sm font-semibold">{m.name}</div>
              </Link>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Main column */}
      <div className="flex-1 min-w-0 h-full min-h-0 flex flex-col">
        {/* Desktop Topbar */}
        <header className="hidden lg:flex h-16 border-b border-white/[0.06] px-6 items-center gap-4 kr-glass sticky top-0 z-30" data-testid="topbar">
          <GlobalSearch modules={modules} />
          <div className="flex-1" />
          <div className="flex items-center gap-2.5">
            {(user?.role === "admin" || user?.role === "super_admin") && (
              <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 text-xs font-mono text-[#8A52FF] hover:text-white px-2 py-1 border border-[#8A52FF]/30 hover:border-[#8A52FF]/60 rounded-sm" data-testid="topbar-admin-btn">
                <Shield className="h-3.5 w-3.5" /> ADMIN
              </button>
            )}
            <button
              onClick={() => navigate("/billing")}
              className="flex items-center gap-1.5 text-xs font-semibold px-3.5 h-9 rounded-full border transition-colors"
              style={{ color: "rgb(var(--ac-yellow))", borderColor: "rgba(255,210,63,0.35)" }}
              data-testid="topbar-upgrade-btn"
            >
              <Crown className="h-3.5 w-3.5" /> Upgrade Plan
            </button>
            <NotificationsBell />
            <UserMenu handleLogout={handleLogout} />
          </div>
        </header>

        {/* Mobile Topbar */}
        <header className="lg:hidden h-14 border-b border-white/[0.06] px-3 flex items-center justify-between kr-glass sticky top-0 z-30 pt-[env(safe-area-inset-top)]" data-testid="mobile-topbar">
          <button onClick={() => setDrawerOpen(true)} className="p-2.5 -ml-1 rounded-md hover:bg-white/5 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Open menu" data-testid="mobile-hamburger">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0 flex-1 justify-center">
            <AlphaCoreLogo size={26} variant="mark" />
            <span className="text-sm font-display font-semibold truncate capitalize" data-testid="mobile-page-title">{pageTitle}</span>
          </div>
          <UserMenu handleLogout={handleLogout} compact />
        </header>

        {/* Content — THE single primary vertical scroll container */}
        <main className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+76px)] lg:pb-0" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Nav — Home · AI Chat · Create · Projects · Profile */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 kr-glass border-t border-white/10 pb-[env(safe-area-inset-bottom)]" data-testid="bottom-nav" aria-label="Primary">
        <div className="grid grid-cols-5 items-end">
          <BottomItem to="/dashboard" icon={HomeIcon} label="Home" k="home" location={location} exact />
          <BottomItem to="/agents/create" icon={MessageCircle} label="AI Chat" k="ai" location={location} />
          <button
            onClick={() => setCreateOpen(true)}
            className="flex flex-col items-center justify-center h-16"
            aria-label="Create"
            data-testid="bottom-nav-create"
          >
            <span className="w-12 h-12 -mt-5 rounded-full ac-rainbow-bg flex items-center justify-center shadow-[0_0_24px_rgba(255,58,168,0.4)]">
              <Plus className="h-5 w-5 text-black" strokeWidth={2.5} />
            </span>
          </button>
          <BottomItem to="/projects" icon={FolderKanban} label="Projects" k="projects" location={location} />
          <BottomItem to="/settings" icon={UserIcon} label="Profile" k="profile" location={location} />
        </div>
      </nav>
    </div>
  );
}

function BottomItem({ to, icon: Icon, label, k, location, exact = false }) {
  const active = exact ? location.pathname === to : location.pathname.startsWith(to);
  return (
    <NavLink
      to={to}
      className={`flex flex-col items-center justify-center gap-1 h-16 min-h-[44px] text-[10px] font-mono uppercase tracking-wider relative ${active ? "text-white" : "text-white/50 hover:text-white"}`}
      data-testid={`bottom-nav-${k}`}
      aria-current={active ? "page" : undefined}
    >
      <Icon className="h-5 w-5" style={active ? { color: "rgb(var(--ac-pink))" } : undefined} />
      {label}
      {active && <span className="absolute bottom-[2px] w-8 h-[2px] rounded-t" style={{ background: "rgb(var(--ac-pink))" }} />}
    </NavLink>
  );
}

function UserMenu({ handleLogout, compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={`flex items-center gap-2 ${compact ? "pl-1 pr-2 py-1 min-w-[44px] min-h-[44px]" : "pl-2 pr-3 py-1.5"} border border-white/10 rounded-full hover:bg-white/5`} data-testid="user-menu-btn" aria-label="Account menu">
          <div className={`${compact ? "w-8 h-8" : "w-7 h-7"} rounded-full ac-rainbow-bg flex items-center justify-center text-xs font-semibold text-black overflow-hidden`}>
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0] || "?").toUpperCase()}
          </div>
          {!compact && (
            <div className="text-left leading-tight">
              <div className="text-xs font-semibold">{user?.name?.split(" ")[0]}</div>
              <div className="text-[10px] text-white/40 capitalize">{user?.plan} plan</div>
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#101219] border-white/10 text-white w-56">
        <DropdownMenuLabel>
          <div className="text-sm truncate">{user?.name}</div>
          <div className="text-xs text-white/40 truncate">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/5" />
        <DropdownMenuItem onClick={() => navigate("/settings")} className="hover:bg-white/5 focus:bg-white/5" data-testid="menu-settings">
          <Settings className="h-4 w-4 mr-2" /> Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/billing")} className="hover:bg-white/5 focus:bg-white/5" data-testid="menu-billing">
          <CreditCard className="h-4 w-4 mr-2" /> Billing · {user?.plan}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/settings/memory")} className="hover:bg-white/5 focus:bg-white/5" data-testid="menu-memory">
          <Sparkles className="h-4 w-4 mr-2" /> Memory
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/5" />
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <DropdownMenuItem onClick={() => navigate("/admin")} className="hover:bg-white/5 focus:bg-white/5 text-[#8A52FF]" data-testid="menu-admin">
            <Shield className="h-4 w-4 mr-2" /> Admin panel
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleLogout} className="hover:bg-white/5 focus:bg-white/5 text-red-300" data-testid="menu-logout">
          <LogOut className="h-4 w-4 mr-2" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
